import { useState, useCallback, useEffect, useRef } from 'react'
import { getNotifications, markAsWatched } from '../../apiClients/notification/notificationClient'
import { useUser } from '../../hooks'
import { 
  saveNotificationsToCache, 
  clearNotificationsCache 
} from '../../utils/notificationCache'
import type { Notification } from '../../types'

const PAGE_SIZE = 20

/**
 * Хук для управления уведомлениями пользователя
 * 
 * Логика загрузки:
 * - Превью уведомлений приходит из /me endpoint при авторизации (через user.notificationWidget)
 * - Полная загрузка уведомлений происходит только при открытии окна (вызов fetchAll)
 * - Пагинация и обновление — по требованию
 * 
 * Функции:
 * - fetchAll: загрузка полного списка при открытии окна уведомлений
 * - loadMore: пагинация
 * - markAsRead: отметка как прочитанных
 * - refresh: принудительное обновление
 */
export const useNotificationState = () => {
  const { user, isLoggedIn } = useUser()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  
  // Флаг: была ли уже попытка загрузки (успешная или нет)
  const fetchAttemptedRef = useRef(false)
  // Отслеживаем для какого userId инициализировано состояние
  const initializedForUserRef = useRef<string | null>(null)

  // Синхронизация превью из user.notificationWidget при изменении пользователя
  useEffect(() => {
    if (!isLoggedIn || !user) {
      // Сброс состояния при выходе
      if (initializedForUserRef.current !== null) {
        setNotifications([])
        setCurrentPage(1)
        setHasMore(true)
        setError(null)
        fetchAttemptedRef.current = false
        clearNotificationsCache()
        initializedForUserRef.current = null
      }
      return
    }

    // Пропускаем, если уже инициализированы для этого пользователя
    if (initializedForUserRef.current === user.id) {
      return
    }

    // Инициализация превью из /me
    initializedForUserRef.current = user.id
    fetchAttemptedRef.current = false
    setError(null)
    
    if (user.notificationWidget) {
      const { notifications: notifData } = user.notificationWidget
      setNotifications(notifData.items)
      setHasMore(notifData.pagination.page < notifData.pagination.totalPages)
      setCurrentPage(1)
    }
  }, [isLoggedIn, user])

  /**
   * Загрузка полного списка уведомлений
   * Вызывается при открытии окна уведомлений
   * Запрос делается только один раз (успешный или с ошибкой)
   */
  const fetchAll = useCallback(async () => {
    if (!user || isLoading) return
    
    // Если уже была попытка загрузки — не делаем повторный запрос
    if (fetchAttemptedRef.current) return

    fetchAttemptedRef.current = true
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await getNotifications(user.id, 1, PAGE_SIZE)
      setNotifications(response.notifications)
      setHasMore(response.notifications.length === PAGE_SIZE)
      setCurrentPage(1)
      saveNotificationsToCache(response.notifications)
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
      setError('fetch_failed')
    } finally {
      setIsLoading(false)
    }
  }, [user, isLoading])

  const markAsRead = useCallback(async (notificationIds: string[]) => {
    if (!user) return

    try {
      await markAsWatched(notificationIds)
      
      setNotifications((prev) => {
        const updated = prev.map(notification =>
          notificationIds.includes(notification.id)
            ? { ...notification, isWatched: true }
            : notification
        )
        saveNotificationsToCache(updated)
        return updated
      })
    } catch (error) {
      console.error('Failed to mark notifications as read:', error)
      throw error
    }
  }, [user])

  const loadMore = useCallback(async () => {
    if (!user || !hasMore || isLoading) return

    setIsLoading(true)
    try {
      const response = await getNotifications(user.id, currentPage + 1, PAGE_SIZE)
      
      if (response.notifications.length === 0) {
        setHasMore(false)
        return
      }

      setNotifications((prev) => {
        const updated = [...prev, ...response.notifications]
        saveNotificationsToCache(updated)
        return updated
      })

      setCurrentPage(prev => prev + 1)
      setHasMore(response.notifications.length === PAGE_SIZE)
    } catch (error) {
      console.error('Failed to load more notifications:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [user, currentPage, hasMore, isLoading])

  const refresh = useCallback(async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const response = await getNotifications(user.id, 1, PAGE_SIZE)
      setNotifications(response.notifications)
      setCurrentPage(1)
      setHasMore(response.notifications.length === PAGE_SIZE)
      saveNotificationsToCache(response.notifications)
      setError(null)
    } catch (err) {
      console.error('Failed to refresh notifications:', err)
      setError('fetch_failed')
    } finally {
      setIsLoading(false)
    }
  }, [user])

  return {
    notifications,
    isLoading,
    hasMore,
    error,
    fetchAll,
    markAsRead,
    loadMore,
    refresh,
  }
}
